<template>
  <Dialog
    v-model:visible="isVisible"
    :header="title"
    :style="{ width: dialogWidth }"
    :modal="true"
    :draggable="false"
    :closable="true"
    :dismissable-mask="dismissableMask"
    class="p-modal"
    @hide="onHide"
    @show="emit('show', $event)"
    @after-show="emit('shown', $event)"
  >
    <template #header>
      <span>
        <span class="p-header-icon"><slot name="header-icon" /></span>
        <span class="p-modal-title-text">{{ title }}</span>
      </span>
    </template>

    <slot name="modal-content" />

    <template #footer>
      <slot name="modal-footer" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import Dialog from 'primevue/dialog'
import { eventBus } from '@/composables/useEventBus'

const props = withDefaults(
  defineProps<{
    title: string
    size?: string
    modalId: string
    hideFooter?: boolean
    classModalContent?: string
    dismissableMask?: boolean
  }>(),
  {
    size: 'md',
    hideFooter: true,
    classModalContent: 'p-modal-content',
    dismissableMask: true,
  },
)

const emit = defineEmits<{
  (e: 'show', evt: Event): void
  (e: 'shown', evt: Event): void
  (e: 'hide', evt: Event): void
  (e: 'hidden', evt: Event): void
  (e: 'close', evt: Event): void
}>()

const isVisible = ref(false)

const dialogWidth = computed(() => {
  switch (props.size) {
    case 'lg':
      return '800px'
    case 'sm':
      return '350px'
    default:
      return '500px'
  }
})

function show() {
  isVisible.value = true
}

function hide() {
  isVisible.value = false
}

function onHide(e: Event) {
  emit('hide', e)
  emit('hidden', e)
  emit('close', e)
}

onMounted(() => {
  eventBus.on(`show:${props.modalId}`, show)
  eventBus.on(`hide:${props.modalId}`, hide)
})

onUnmounted(() => {
  eventBus.off(`show:${props.modalId}`, show)
  eventBus.off(`hide:${props.modalId}`, hide)
})

defineExpose({ show, hide })
</script>

<style lang="scss" scoped>
@use '../assets/scss/variables' as *;

.p-header-icon {
  padding-right: 1rem;
}

.p-modal-title-text {
  font-weight: 500;
  color: $dark-color;
  text-transform: uppercase;
}
</style>
